import { useEffect, useMemo, useRef } from 'react';
import { useResizeObserver } from '../../../hooks/useResizeObserver.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { drawReportGraph } from '../drawReportGraph.ts';
import { ReportSettings } from '../report-settings/reportSettings.ts';
import { ReportGraphContainer } from '../reportGraphContainer.tsx';
import { prepareMeasurementDelays } from './prepareMeasurementDelays.ts';


interface Props {
    style?: React.CSSProperties;
    reportSettings: ReportSettings;
    operatorFooterLabelsByPeriod?: string[][];
    onOperatorFooterClick?: (periodIndex: number) => void;
}

export function MeasurementDelaysGraph({
    style,
    reportSettings,
    operatorFooterLabelsByPeriod,
    onOperatorFooterClick,
}: Props) {
    const graphContainerRef = useRef<HTMLDivElement>(null);
    const dimensions = useResizeObserver(graphContainerRef);

    const { data: delaysData, isFetching } = trpc.graphsData.getMeasurementDelays.useQuery({
        targetTime: {
            start: reportSettings.first.start,
            end: reportSettings.last.end,
        }
    }, { refetchOnWindowFocus: false, });


    const preparedData = useMemo(() => {
        if (!delaysData) return undefined;
        return prepareMeasurementDelays(delaysData, reportSettings);
    }, [delaysData, reportSettings]);

    useEffect(() => {
        if (!graphContainerRef.current || !preparedData) return;
        drawReportGraph(graphContainerRef.current!, preparedData, operatorFooterLabelsByPeriod, onOperatorFooterClick);
    }, [graphContainerRef, preparedData, operatorFooterLabelsByPeriod, onOperatorFooterClick, dimensions]);


    return (
        <ReportGraphContainer
            style={{
                ...style,
                height: '100%',
            }}
            containerRef={graphContainerRef}
            isLoading={isFetching}
        />
    )
}
